import React, { useState } from 'react';
import { Button, Dialog, Spinner } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { useLocation, useNavigate } from 'react-router-dom';

import useDimensions from '~/app/shared/dimensions/hooks/useDimensions';
import { BulkDimensionsLocationState } from '../types';
import { BulkDimensionForm } from './BulkDimensionForm';

interface BulkDimensionsProps {
    onSubmitted: () => void;
}

export const BulkDimensionDialog = ({ onSubmitted }: BulkDimensionsProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { draftId, selectedLineIds } = (location.state || {}) as BulkDimensionsLocationState;
    const [loading, setLoading] = useState(false);
    const { __initialized: dimensionsInitialized, data: dimensions, getDimensionName } = useDimensions();

    const navigateBack = () => navigate(location.pathname);

    const getDialogContent = () => {
        if (loading || !dimensionsInitialized) {
            return (
                <Dialog.Content aria-label={t`Loading`}>
                    <Spinner className="w-full h-full flex justify-center" />
                </Dialog.Content>
            );
        }

        if (!selectedLineIds || selectedLineIds.length === 0) {
            let title = t`Cannot update dimensions`;
            if (dimensions.length === 1) {
                const name = getDimensionName(dimensions[0].id);
                title = t`Cannot update ${name}`;
            }

            return (
                <Dialog.Content aria-label={title}>
                    <Dialog.Title>{title}</Dialog.Title>

                    <p>{t`You must select at least one line.`}</p>

                    <Dialog.Footer>
                        <Button appearance="primary" autoFocus onClick={navigateBack}>
                            <Trans>OK</Trans>
                        </Button>
                    </Dialog.Footer>
                </Dialog.Content>
            );
        }

        let title = t`Update dimensions`;
        if (dimensions.length === 1) {
            const name = getDimensionName(dimensions[0].id);
            title = t`Update ${name}`;
        }

        return (
            <Dialog.Content aria-label={title}>
                <Dialog.Title>{title}</Dialog.Title>
                <BulkDimensionForm
                    initialData={{ draftId, selectedLineIds }}
                    dimensions={dimensions}
                    onSubmitted={onSubmitted}
                    setLoading={setLoading}
                    navigateBack={navigateBack}
                />
            </Dialog.Content>
        );
    };

    return (
        <Dialog size="xs" onClose={navigateBack} open={location.hash === '#bulk-dimension'} draggable>
            {getDialogContent()}
        </Dialog>
    );
};
